// Copyright (c) 2026 AesirX.io Ltd. All rights reserved.
// This source code is proprietary and confidential.
// See LICENSE.md for licensing terms.

/**
 * Sprint 25.3.4: L2 sidebar category groupings.
 *
 * Sprint 61.0.10 — extracted verbatim from `layouts/AdminLayout.tsx` so the
 * dashboard's Module Status strip can read the same five categories the
 * sidebar renders, and given one `icon` per category: the `MenuIcon` key the
 * category header renders before its label. `agent-os/scripts/help/
 * export-sidebar-menu-tree.ts` parses this declaration by shape (a regex over
 * the source, not an import) — keep it a literal array of `{ key, modules,
 * icon }` objects in this order (`flattenSections: true` may follow `icon`),
 * closed with `as const`, and do not quote the declaration's opening in a
 * comment: the parser takes the first match.
 *
 * Sprint 63.42.2 — `flattenSections: true` declares a category whose single
 * section is the category: the sidebar renders that section's groups directly
 * under the category header, so the menu never reads *Sector Overlays ›
 * Sector Overlays* (OR-26 §2). A section may also name its category itself
 * (`MenuItem.category` in AdminLayout) when its first leaf's path would file it
 * elsewhere; such a section is not listed under `modules` here.
 */
export const SIDEBAR_CATEGORIES = [
  {
    key: 'data_privacy_rights',
    modules: [
      '/admin/consent',
      '/admin/consent-properties',
      '/admin/classification',
      '/admin/data-subjects',
      '/admin/discovery',
      '/admin/data-mapping',
      '/admin/rights-requests',
    ],
    icon: 'category_privacy',
  },
  {
    key: 'vendor_third_party',
    modules: ['/admin/governance', '/admin/vendor-risk'],
    icon: 'category_vendor',
  },
  {
    key: 'risk_assessment',
    modules: ['/admin/assessments', '/admin/audit', '/admin/incidents', '/admin/privacy-scanner'],
    icon: 'category_risk',
  },
  {
    key: 'operations_automation',
    modules: [
      '/admin/forms',
      '/admin/connectors',
      '/admin/deletion-orchestration',
      '/admin/incident-ops',
      // Sprint 63.42.2 — Localization, then the Regulatory section, which
      // declares this category on itself (its first leaf is a Localization URL).
      '/admin/localization',
      '/admin/monitoring',
      '/admin/program-governance',
      '/admin/cmp-integrations',
    ],
    icon: 'category_operations',
  },
  {
    // Sprint 63.42.2 (owner request 2026-11-01; OR-26) — Sector Overlays, a
    // category of its own. Its one section is Module 30's; the category is
    // absent wherever that section is (Light and Standard: module off). The
    // section's first leaf is the Overview, so its derived path is this one;
    // the Overlay Catalog keeps its `/admin/localization/sector-overlays` URL.
    key: 'sector_overlays',
    modules: ['/admin/sector-overlays'],
    icon: 'category_sector_overlays',
    flattenSections: true,
  },
  {
    key: 'task_workflow',
    modules: [
      '/admin/access-accountability',
      '/admin/organizations',
      '/admin/partner',
      '/admin/tasks',
    ],
    icon: 'category_workflow',
  },
] as const;

export type SidebarCategory = (typeof SIDEBAR_CATEGORIES)[number];
export type SidebarCategoryKey = SidebarCategory['key'];
export type SidebarCategoryIcon = SidebarCategory['icon'];

/** Whether the category renders its section's groups directly under its header. */
export function categoryFlattensSections(category: SidebarCategory): boolean {
  return 'flattenSections' in category && category.flattenSections === true;
}
